"use client";

import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Tag,
  Space,
  message,
} from "antd";
import {
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { getAllEventApi } from "../../../redux/api/api";

const { Search } = Input;

const EventsListTable = () => {
  const [events, setEvents] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();

  const getAllEvent = async () => {
    setLoading(true);
    try {
      const res = await getAllEventApi();
      if (res.status === 200) {
        setEvents(res.data.info || []);
        setFilteredData(res.data.info || []);
      }
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllEvent();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = events.filter((ev) =>
      ev.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredData(filtered);
  };

  const handleApprove = async (id) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/event/updateventstatus`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, status: "approved" }),
        }
      );
      if (res.ok) {
        message.success("✅ Event Approved!");
        getAllEvent();
      } else {
        message.error("❌ Failed to approve event");
      }
    } catch (err) {
      console.error("Error approving event", err);
      message.error("❌ Error approving event");
    }
  };

  const handleReject = async (id) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/event/updateventstatus`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, status: "rejected" }),
        }
      );
      if (res.ok) {
        message.success("✅ Event Rejected!");
        getAllEvent();
      } else {
        message.error("❌ Failed to reject event");
      }
    } catch (err) {
      console.error("Error rejecting event", err);
      message.error("❌ Error rejecting event");
    }
  };

  const columns = [
    {
      title: "Event ID",
      dataIndex: "id",
      key: "id",
      render: (id) => `#${id?.toString().slice(-4)}`,
    },
    {
      title: "Event Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: "Event Type",
      dataIndex: "eventType",
      key: "eventType",
      render: (type) =>
        type
          ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
          : "-",
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
    },
    // {
    //   title: "Category",
    //   dataIndex: "category",
    //   key: "category",
    // },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color =
          status === "approved"
            ? "green"
            : status === "rejected"
              ? "red"
              : "orange";
        let label =
          status === "saved"
            ? "Pending"
            : status.charAt(0).toUpperCase() + status.slice(1);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => navigate(`/events/details/${record.id}`)}
          >
            View Details
          </Button>
          {/* Edit button - navigates to create-an-event and passes eventId */}
          <Button
            type="link"
            onClick={() =>
              navigate("/events/create-an-event", { state: { eventId: record.id } })
            }
          >
            Edit
          </Button>
          {record.status === "approved" ? (
            <Button
              icon={<CloseOutlined />}
              danger
              size="small"
              onClick={() => handleReject(record.id)}
            >
              Reject
            </Button>
          ) : record.status === "rejected" ? (
            <Button
              icon={<CheckOutlined />}
              type="primary"
              size="small"
              onClick={() => handleApprove(record.id)}
            >
              Approve
            </Button>
          ) : (
            <>
              <Button
                icon={<CheckOutlined />}
                type="primary"
                size="small"
                onClick={() => handleApprove(record.id)}
              >
                Approve
              </Button>
              <Button
                icon={<CloseOutlined />}
                danger
                size="small"
                onClick={() => handleReject(record.id)}
              >
                Reject
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: "8px",
        padding: "20px",
        background: "#fff",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Search
          placeholder="Search event here..."
          onSearch={handleSearch}
          onChange={(e) => handleSearch(e.target.value)}
          value={searchText}
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 250 }}
        />
        <Link to="/events/create-an-event">
          <Button type="primary" icon={<PlusOutlined />}>
            Add New Event
          </Button>
        </Link>
      </div>

      {/* Table */}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} events`,
        }}
        bordered
      />
    </Card>
  );
};

export default EventsListTable;

